import React from "react";
import { LinkObject, NodeObject } from "react-force-graph-2d";
import { TCanvasNode } from "../types/TCanvasNode";
import { TCanvasLink } from "../types/TCanvasLink";

const HOVER_DELAY_TIME = 300


export default function useItemHover() {
  const timerId = React.useRef<ReturnType<typeof setTimeout> | null>(null)
  const prevState = React.useRef<boolean>(false)
  const shouldGreyOutNodes = React.useRef(false)
  const pinnedNode = React.useRef<NodeObject<NodeObject<TCanvasNode>> | null>(null)

  const triggerDelayed = (func: any) => {
    if (timerId.current) {
      clearTimeout(timerId.current)
      timerId.current = null
    }
    if (prevState.current) {
      prevState.current = func()
      timerId.current = null
    } else {
      timerId.current = setTimeout(() => {
        prevState.current = func()
        timerId.current = null
      }, HOVER_DELAY_TIME)
    }
  }

  const updateNodeHover = (
    node: NodeObject<NodeObject<TCanvasNode>> | null,
    prevNode: NodeObject<NodeObject<TCanvasNode>> | null,
    greyOut: boolean = true
  ) => {
    return () => {
      if (node?.node_type == "DEFAULT_DOMAIN") return
      if (node != null && prevNode != null) {
        prevNode.hovered = false;
        prevNode.links?.forEach(link => {
          if (typeof link == 'object') link.hovered = false
          if (typeof link.source == "object") link.source.hovered = false
          if (typeof link.target == "object") link.target.hovered = false
        })
        node.hovered = true; // Enable hover effect for all node types
        let hasLinks = false;
        node.links?.forEach(link => {
          if (link) hasLinks = true
          if (typeof link == 'object') link.hovered = true
          if (typeof link.source == "object") link.source.hovered = true
          if (typeof link.target == "object") link.target.hovered = true
        })
        if(greyOut) {
          shouldGreyOutNodes.current = hasLinks
        }
      } else if (node) {
        node.hovered = true; // Enable hover effect for all node types
        let hasLinks = false;
        node.links?.forEach(link => {
          if (link) hasLinks = true
          if (typeof link == 'object') link.hovered = true
          if (typeof link.source == "object") link.source.hovered = true
          if (typeof link.target == "object") link.target.hovered = true
        })
        if(greyOut) {
          shouldGreyOutNodes.current = hasLinks
        }
      } else if (prevNode) {
        prevNode.hovered = false;
        prevNode.links?.forEach(link => {
          if (typeof link == 'object') link.hovered = false
          if (typeof link.source == "object") link.source.hovered = false
          if (typeof link.target == "object") link.target.hovered = false
        })
        if(greyOut) {
          shouldGreyOutNodes.current = false
        }
      }
      return shouldGreyOutNodes.current
    }
  }

  const updateLinkHover = (
    link: LinkObject<NodeObject<TCanvasNode>, LinkObject<TCanvasNode, TCanvasLink>> | null,
    prevLink: LinkObject<NodeObject<TCanvasNode>, LinkObject<TCanvasNode, TCanvasLink>> | null
  ) => {
    return () => {
      if (link) {
        link.hovered = true;
        shouldGreyOutNodes.current = true;
      }
      if (prevLink) {
        prevLink.hovered = false;
        shouldGreyOutNodes.current = true;
      }

      if (typeof prevLink?.source == 'object' && typeof prevLink?.target == 'object') {
        prevLink.source.hovered = false;
        shouldGreyOutNodes.current = false;
        prevLink.target.hovered = false;
      }
      if (typeof link?.source == 'object' && typeof link?.target == 'object') {
        link.source.hovered = true;
        shouldGreyOutNodes.current = true;
        link.target.hovered = true;
      }
      return shouldGreyOutNodes.current
    }
  }

  const handleNodePinning = (node?: NodeObject<NodeObject<TCanvasNode>>) => {
    if(node == undefined) {
      updateNodeHover(null, pinnedNode.current)
      pinnedNode.current = null
      return
    }
    if (node.node_type == "COLUMN") {
      if (pinnedNode.current == null) {
        updateNodeHover(node, null)()
        pinnedNode.current = node
      } else if (node.node_id == pinnedNode.current.node_id) {
        updateNodeHover(null, node)()
        pinnedNode.current = null
      } else {
        updateNodeHover(node, pinnedNode.current)()
        pinnedNode.current = node
      }
    }
  }


  return { shouldGreyOutNodes, updateNodeHover, updateLinkHover, triggerDelayed, pinnedNode, handleNodePinning };
}
